import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";

import { demoSignals } from "@/data/demoData";
import { supabase } from "@/integrations/supabase/client";
import { getCoachLeaderIds } from "@/lib/coachLeaderScope";
import { appQueryKeys } from "@/lib/queryKeys";
import type { CoachOverviewSignalType } from "@/lib/behavior/viewModels/buildCoachVeeraEntry";

type ReadinessLevel = "low" | "moderate" | "high";

type LeaderBehaviorSummary = {
  id?: string | null;
  phase?: string | null;
  avg_rating_14d?: number | null;
  streak_current?: number | null;
  total_practice_days?: number | null;
  threshold_met_at?: string | null;
  plateau_detected_at?: string | null;
  last_practice_date?: string | null;
  practice_start_date?: string | null;
};

type LeaderProfileWithBehaviors = {
  id: string;
  full_name?: string | null;
  leader_behaviors?: LeaderBehaviorSummary[] | null;
};

export interface CoachAttentionReadinessInfo {
  level: ReadinessLevel;
  reasonSummary: string;
  supportingSignals: string[];
  considerations: {
    continue: string;
    review: string;
  };
}

export interface CoachAttentionSignal {
  leaderId: string;
  leaderName: string;
  signalType: CoachOverviewSignalType;
  signalLabel: string;
  reason: string;
  severity: "low" | "medium" | "high";
  ctaLabel: string;
  ctaRoute: string;
  readiness?: CoachAttentionReadinessInfo;
}

function buildDemoAttentionSignals(): CoachAttentionSignal[] {
  return demoSignals.map((signal) => ({
    leaderId: signal.leaderId,
    leaderName: signal.leaderName,
    signalType: signal.signalType === "graduation_check"
      ? "graduation_review"
      : signal.signalType === "sequence_check"
        ? "sequence_check"
        : "engagement_trend",
    signalLabel: signal.signalType === "graduation_check"
      ? "Graduation"
      : signal.signalType === "sequence_check"
        ? "Sequencing"
        : "Review",
    reason: signal.reason,
    severity: signal.severity,
    ctaLabel: "Review",
    ctaRoute: `/coach/leaders/${signal.leaderId}`,
    readiness: {
      level: signal.severity === "high" ? "high" : signal.severity === "medium" ? "moderate" : "low",
      reasonSummary: signal.reason,
      supportingSignals: [`${signal.behaviorName} behavior active`],
      considerations: {
        continue: "Continue current approach",
        review: "Review and assess next steps",
      },
    },
  }));
}

export function useCoachAttentionSignals(params: {
  coachId?: string;
  isDemoMode: boolean;
  enabled?: boolean;
}) {
  const { coachId, isDemoMode, enabled = true } = params;

  return useQuery({
    queryKey: appQueryKeys.coachAttentionSignals(coachId, isDemoMode),
    queryFn: async (): Promise<CoachAttentionSignal[]> => {
      if (isDemoMode) return buildDemoAttentionSignals();
      if (!coachId) throw new Error("No coach");

      const signals: CoachAttentionSignal[] = [];
      const leaderIds = await getCoachLeaderIds(coachId);
      const { data: leaders } = leaderIds.length > 0
        ? await supabase
            .from("profiles")
            .select(`
              id,
              full_name,
              leader_behaviors (
                id,
                phase,
                last_practice_date,
                streak_current,
                avg_rating_14d,
                plateau_detected_at,
                practice_start_date,
                total_practice_days,
                threshold_met_at
              )
            `)
            .in("id", leaderIds)
        : { data: [] as LeaderProfileWithBehaviors[] };

      const fourWeeksAgo = subDays(new Date(), 28);

      leaders?.forEach((leader) => {
        const activeBehaviors = leader.leader_behaviors?.filter(
          (row) => row.phase === "ACTIVE_PRACTICE" || row.phase === "CONSOLIDATION",
        ) || [];

        activeBehaviors.forEach((behavior) => {
          const hasThresholdMet = Boolean(behavior.threshold_met_at);
          const avgRating = behavior.avg_rating_14d || 0;
          const streakCurrent = behavior.streak_current || 0;
          const totalDays = behavior.total_practice_days || 0;
          const hasPlateau = Boolean(behavior.plateau_detected_at);

          if (behavior.phase === "ACTIVE_PRACTICE" && hasThresholdMet) {
            const supportingSignals: string[] = [];
            let readinessScore = 0;
            if (hasThresholdMet) {
              supportingSignals.push("Observation threshold met");
              readinessScore += 1;
            }
            if (avgRating >= 3.5) {
              supportingSignals.push(`Leader reflection signal (avg ${avgRating.toFixed(1)})`);
              readinessScore += avgRating >= 4 ? 2 : 1;
            }
            if (streakCurrent >= 5) {
              supportingSignals.push(`Consecutive reflection days (${streakCurrent})`);
              readinessScore += 1;
            }
            if (totalDays >= 14) {
              supportingSignals.push(`Sustained reflections (${totalDays} days)`);
              readinessScore += 1;
            }

            const readinessLevel: ReadinessLevel = readinessScore >= 4 ? "high" : readinessScore >= 2 ? "moderate" : "low";
            if (readinessScore >= 2) {
              signals.push({
                leaderId: leader.id,
                leaderName: leader.full_name || "Unknown",
                signalType: "sequence_check",
                signalLabel: "Sequencing",
                reason: "Evidence density suggests coach review may be useful.",
                severity: readinessLevel === "high" ? "medium" : "low",
                ctaLabel: "Review",
                ctaRoute: `/coach/review/sequence/${leader.id}?type=sequencing&level=${readinessLevel}`,
                readiness: {
                  level: readinessLevel,
                  reasonSummary: "Signals from recent moments suggest coach review may be useful.",
                  supportingSignals,
                  considerations: {
                    continue: "Continue current focus area to collect more moments",
                    review: "Explore sequencing options based on coach context",
                  },
                },
              });
            }
          }

          if (behavior.phase === "CONSOLIDATION") {
            const supportingSignals: string[] = [];
            let readinessScore = 0;
            if (avgRating >= 4) {
              supportingSignals.push(`Leader reflection signal (avg ${avgRating.toFixed(1)})`);
              readinessScore += 2;
            } else if (avgRating >= 3.5) {
              supportingSignals.push(`Leader reflection signal (avg ${avgRating.toFixed(1)})`);
              readinessScore += 1;
            }
            if (hasPlateau) {
              supportingSignals.push("Pattern plateau observed");
              readinessScore += 1;
            }
            if (streakCurrent >= 7) {
              supportingSignals.push(`Consecutive reflection days (${streakCurrent})`);
              readinessScore += 1;
            }
            if (totalDays >= 21) {
              supportingSignals.push(`Extended reflection history (${totalDays} days)`);
              readinessScore += 1;
            }

            const readinessLevel: ReadinessLevel = readinessScore >= 4 ? "high" : readinessScore >= 2 ? "moderate" : "low";
            if (readinessScore >= 2) {
              signals.push({
                leaderId: leader.id,
                leaderName: leader.full_name || "Unknown",
                signalType: "graduation_review",
                signalLabel: "Graduation",
                reason: "Evidence density suggests this focus area may be consolidating.",
                severity: readinessLevel === "high" ? "medium" : "low",
                ctaLabel: "Review",
                ctaRoute: `/coach/review/sequence/${leader.id}?type=graduation&level=${readinessLevel}`,
                readiness: {
                  level: readinessLevel,
                  reasonSummary: "Signals from recent moments suggest this focus area may be ready for synthesis review.",
                  supportingSignals,
                  considerations: {
                    continue: "Extend consolidation to gather more moments",
                    review: "Review whether synthesis phase is appropriate",
                  },
                },
              });
            }
          }

          if (behavior.last_practice_date) {
            const lastPractice = new Date(behavior.last_practice_date);
            const daysSince = Math.floor((Date.now() - lastPractice.getTime()) / (1000 * 60 * 60 * 24));
            if (daysSince >= 5) {
              signals.push({
                leaderId: leader.id,
                leaderName: leader.full_name || "Unknown",
                signalType: "engagement_trend",
                signalLabel: "Engagement trend",
                reason: `There is evidence of reduced engagement (${daysSince} days since last recorded moment). Coach review may be useful.`,
                severity: daysSince >= 10 ? "high" : daysSince >= 7 ? "medium" : "low",
                ctaLabel: "Review",
                ctaRoute: `/coach/leaders/${leader.id}`,
              });
            }
          }

          if (behavior.practice_start_date && behavior.phase === "ACTIVE_PRACTICE") {
            const startDate = new Date(behavior.practice_start_date);
            const daysPracticing = Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24));
            if (daysPracticing >= 30 && !behavior.threshold_met_at && startDate <= fourWeeksAgo) {
              signals.push({
                leaderId: leader.id,
                leaderName: leader.full_name || "Unknown",
                signalType: "prolonged_practice",
                signalLabel: "Extended focus",
                reason: `This focus area has been active for ${daysPracticing} days. Coach review may be useful.`,
                severity: daysPracticing >= 45 ? "medium" : "low",
                ctaLabel: "Review",
                ctaRoute: `/coach/leaders/${leader.id}`,
              });
            }
          }
        });
      });

      signals.sort((a, b) => {
        if (a.severity === "high" && b.severity !== "high") return -1;
        if (b.severity === "high" && a.severity !== "high") return 1;
        return 0;
      });

      return signals;
    },
    enabled: Boolean(enabled && (coachId || isDemoMode)),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
}
